import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ColorsV2,
  Divider,
  Flex,
  Icon,
  Text,
  TabButtonGroup,
  spaces,
} from '@electricjs/arc';

import { ThemeContext, Themes } from '../ThemeProvider';

const isDevOrStaging =
  window._env_?.VITE_ENV_NAME === 'local' ||
  window._env_?.VITE_ENV_NAME === 'staging';

const ThemeSwitcher = ({
  shouldHaveBottomDivider = true,
}: {
  shouldHaveBottomDivider?: boolean;
}) => {
  const { t } = useTranslation('components');
  const { themeName, setThemeValue } = useContext(ThemeContext);

  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSynthwaveActive = themeName === Themes.SYNTHWAVE;
  const systemLabel = isSynthwaveActive ? 'Ryami' : t('themeSwitcher.system');

  const handleTabChange = (val: string) => {
    if (!isDevOrStaging || val !== Themes.AUTO) {
      setThemeValue(val);
      return;
    }

    clickCountRef.current++;

    if (clickCountRef.current === 1) {
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 800);
    }

    if (clickCountRef.current >= 3) {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickCountRef.current = 0;
      setThemeValue(Themes.SYNTHWAVE);
      return;
    }

    setThemeValue(val);
  };

  return (
    <>
      <Flex px={spaces.space_m} pb={spaces.space_xs} alignItems="center">
        <Icon
          icon="palette"
          size="small"
          mr={spaces.space_xs}
          color={ColorsV2.TEXT}
        />
        <Text variant="body-2">{t('themeSwitcher.label')}</Text>
        <Text variant="legal" intent="info" ml={spaces.space_s}>
          {t('themeSwitcher.beta')}
        </Text>
      </Flex>
      <Flex p={`0 ${spaces.space_s} ${spaces.space_s}`} justifyContent="center">
        <TabButtonGroup
          tabs={[
            { value: Themes.LIGHT, label: t('themeSwitcher.light') },
            { value: Themes.AUTO, label: systemLabel },
            { value: Themes.DARK, label: t('themeSwitcher.dark') },
          ]}
          activeTabValue={
            isSynthwaveActive ? Themes.AUTO : themeName || Themes.LIGHT
          }
          onTabChange={handleTabChange}
        />
      </Flex>
      {shouldHaveBottomDivider && <Divider space={spaces.space_xs} />}
    </>
  );
};

export default ThemeSwitcher;
